import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";
import type { ExperienceTourV2 } from "@/app/(main)/components/ExperienceCardV2";

// ─── API Response ───────────────────────────────────────────────────

interface TourApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: ExperienceTourV2[];
}

// ─── Re-exports ─────────────────────────────────────────────────────

export type { ExperienceTourV2 };

// ─── Server-side Data Fetching ──────────────────────────────────────

export const fetchExperiences = cache(
  async (
    page = 1,
    pageSize = 10,
  ): Promise<{
    items: ExperienceTourV2[];
    totalPages: number;
    count: number;
  }> => {
    const res = await api.get<TourApiResponse>(ApiRoutes.INVENTORY_TOUR, {
      params: {
        IsWebsite: true,
        PageNumber: page,
        PageSize: pageSize,
      },
    });

    const items = (res.data?.data ?? []).filter((tour) =>
      Boolean(tour.region?.destinationId),
    );

    return {
      items,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchExperiencesClient(
  page = 1,
  pageSize = 10,
): Promise<{
  items: ExperienceTourV2[];
  totalPages: number;
  count: number;
}> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    IsWebsite: "true",
    CompanyId: COMPANY_ID,
  });

  const res = await fetch(`${BASE_URL}/inventory/tour?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });

  const json = await res.json();
  const items: ExperienceTourV2[] = (json.data ?? []).filter(
    (tour: ExperienceTourV2) => Boolean(tour.region?.destinationId),
  );

  return {
    items,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}
